// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { Construction, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileActivityTab } from "@/features/profile/profile-activity-tab";
import { ProfileInfoTab } from "@/features/profile/profile-info-tab";
import { ProfileSecurityTab } from "@/features/profile/profile-security-tab";
import { ProfileSettingsTab } from "@/features/profile/profile-settings-tab";
import { useDebugMode } from "@/lib/debug-mode";

interface ProfileDetailProps {
  tab?: string;
  initialFilters?: string;
}

export function ProfileDetail({ tab, initialFilters }: ProfileDetailProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { debugMode } = useDebugMode();

  // Edit (WIP placeholder) and Activity (mock data) are work-in-progress — debug-mode only.
  const requestedTab = tab ?? "info";
  const tabAllowed: Record<string, boolean> = {
    info: true,
    edit: debugMode,
    security: true,
    settings: true,
    activity: debugMode,
  };
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  function handleTabChange(newTab: string) {
    navigate({
      to: "/profile",
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  return (
    <div>
      <PageHeader title={t("profile.title")} icon={UserRound} />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
              {debugMode && <TabsTrigger value="edit">{t("tabs.edit")}</TabsTrigger>}
              <TabsTrigger value="security">{t("tabs.security")}</TabsTrigger>
              <TabsTrigger value="settings">{t("tabs.settings")}</TabsTrigger>
              {debugMode && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <ProfileInfoTab />
          </TabsContent>
          {debugMode && (
            <TabsContent value="edit" className="space-y-4">
              <div className="max-w-2xl space-y-4 p-6">
                <div className="flex items-center gap-2 rounded-md bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                  <Construction className="h-3.5 w-3.5 shrink-0" />
                  {t("profile.edit.wip")}
                </div>
              </div>
            </TabsContent>
          )}
          <TabsContent value="security" className="space-y-4">
            <ProfileSecurityTab />
          </TabsContent>
          <TabsContent value="settings" className="space-y-4">
            <ProfileSettingsTab />
          </TabsContent>
          {debugMode && (
            <TabsContent value="activity" className="space-y-4">
              <ProfileActivityTab initialFilters={activeTab === "activity" ? initialFilters : undefined} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
